import React, {
  useCallback,
  memo,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Animated, StyleSheet, View } from "react-native";
import Text from "@components/CustomText";
import color from "@constants/color";

interface SearchableListProps {
  data: any[];
  ListHeader: React.ReactElement;
  renderItem: ({ item }: { item: any }) => React.ReactElement;
  scrollY: Animated.Value;
  containerHeight: number;
  emptyText?: string;
}

export interface SearchableListRef {
  scrollToTop: () => void;
}

const EmptyListComponent = memo(({ text }: { text: string }) => (
  <View style={styles.emptyView}>
    <Text variant="heading_24" style={styles.emptyText}>
      {text}
    </Text>
  </View>
));

const SearchableList = memo(
  forwardRef<SearchableListRef, SearchableListProps>(
    (
      { data, ListHeader, renderItem, scrollY, containerHeight, emptyText },
      ref,
    ) => {
      const flatListRef = useRef<any>(null);
      const keyExtractor = useCallback((item: any) => item.id, []);

      useImperativeHandle(ref, () => ({
        scrollToTop: () => {
          flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
        },
      }));
      return (
        <Animated.FlatList
          ref={flatListRef}
          data={data}
          ListHeaderComponent={ListHeader}
          ListEmptyComponent={<EmptyListComponent text={emptyText || ""} />}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          showsVerticalScrollIndicator={false}
          onScroll={Animated.event(
            [{ nativeEvent: { contentOffset: { y: scrollY } } }],
            { useNativeDriver: true },
          )}
          initialNumToRender={10}
          maxToRenderPerBatch={10}
          scrollEventThrottle={8}
          contentContainerStyle={{ paddingVertical: containerHeight || 320 }}
          getItemLayout={(_, index) => ({
            length: 100,
            offset: 100 * index,
            index,
          })}
        />
      );
    },
  ),
);

const styles = StyleSheet.create({
  emptyText: {
    marginTop: "15%",
    textAlign: "center",
    color: color.black06,
  },
  emptyView: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default SearchableList;
